/* eslint-disable no-unused-expressions */
/* eslint-disable no-sequences */
import { getCookie, setCookie, appendScript, createId, isArray, log } from '@/utils';
import request from '@/api/request';

function isAnalyticsId(id = '') {
	return /^UA-/i.test(id);
}

function isGa4Id(id = '') {
	return /^G-/i.test(id);
}

function isAnalyticsOrGtagId(str = '') {
	return isGa4Id(str) || isAnalyticsId(str);
}

function getFromGoogleTagManager() {
	for (const item of Object.keys(window.google_tag_manager ?? {})) {
		if (isAnalyticsOrGtagId(item)) {
			return item;
		}
	}
}

function getFromDataLayer() {
	if (isArray(window.dataLayer)) {
		for (const item of window.dataLayer) {
			if (item[0] === 'config' && isAnalyticsOrGtagId(item[1])) {
				return item[1];
			}
		}
	}
}

function getLegacyAnalyticsId() {
	return window.ga.getAll()[0].get('trackingId');
}

export function getAnalyticsIdFromPage() {
	const getters = [getFromGoogleTagManager, getFromDataLayer, getLegacyAnalyticsId];
	for (const get of getters) {
		try {
			if (get()) {
				return get();
			}
		} catch (e) {}
	}
	return null;
}

function sendGa4WhenReady(fn) {
	if (ga4IsActive()) {
		fn();
		return;
	}

	let count = 0;
	const interval = setInterval(() => {
		if (ga4IsActive()) {
			fn();
			clearInterval(interval);
		}

		if (count > 10) {
			clearInterval(interval);
		}
		count++;
	}, 100);
}

function sendLegacyGaWhenReady(fn = () => {}) {
	if (window.ga) {
		return fn();
	}

	let count = 0;
	const interval = setInterval(() => {
		if (window.ga) {
			fn();
			clearInterval(interval);
		}

		if (count > 10) {
			clearInterval(interval);
		}
		count++;
	}, 100);
}

function handleGa4Post() {
	if (window.gtag) {
		window.gtag(arguments);
	}

	if (isArray(window.dataLayer)) {
		window.dataLayer.push(arguments);
	}
}

function ga4IsActive() {
	return window.gtag || isArray(window.dataLayer);
}

const gtagConfig = {
	send_page_view: false,
	cookie_prefix: 'mop',
	cookie_expires: 60 * 60 * 24 * 365,
	allow_google_signals: false
};

const legacyGaConfig = {
	name: 'mopinion',
	cookieExpires: 60 * 60 * 24 * 365,
	cookieName: 'mop_ga'
};

function loadGa4(id) {
	appendScript(`https://www.googletagmanager.com/gtag/js?id=${id}`, {
		onLoad: () => {
			window.dataLayer = window.dataLayer || [];
			window.gtag = function () {
				window.dataLayer.push(arguments);
			};
			window.gtag('js', new Date());
			window.gtag('config', id, gtagConfig);
		}
	});
}

export default class Track {
	constructor({ advanced, disablePost, formKey, formName, metricsDomain } = {}) {
		this.disablePost = disablePost;
		this.surveyKey = formKey;
		this.surveyName = formName;
		this.metricsDomain = metricsDomain;
		this.customGaTracker = '';
		this.eventProps = {};
		this.feedback = [];

		if (advanced?.logMetrics) {
			this.metrics = true;
		}

		if (advanced?.analytics_integration?.load_analytics) {
			this.gaId = advanced.analytics_integration.GA_TRACKING_ID;
			if (!this.gaId) {
				this.gaId = getAnalyticsIdFromPage();
			}

			this.gaSendFeedback = Boolean(advanced.analytics_integration.sendFeedback);

			if (this.gaId) {
				log(`Tracking Analytics ${this.gaId}`);
				this.initGa();
			}
		}
	}

	setEventProps = props => {
		this.eventProps = props;
		return this;
	};

	setFeedback = props => {
		this.feedback = props;
		return this;
	};

	//General
	send = event => {
		if (this.disablePost) {
			log('Tracking disabled', event, this.eventProps);
			return;
		}

		if (this.metrics) {
			this.sendMetrics(event, this.eventProps);
		}

		if (this.gaId) {
			this.sendGa({ action: event }, this.eventProps);
		}
	};

	sendFeedback = () => {
		if (this.gaId && this.gaSendFeedback) {
			this.sendFeedbackGa(this.feedback);
		}
	};

	//Queso metrics
	sendMetrics = (event, eventProps) => {
		if (!getCookie('SQsession')) {
			setCookie('SQsession', createId(5), 0.5 / 24); /* set cookie for 30mins (?) */
		}

		setTimeout(() => {
			const data = {
				project_token: this.surveyKey,
				datetime: new Date(
					new Date().getTime() - new Date().getTimezoneOffset() * 60000
				).toISOString(),
				session: getCookie('SQsession'),
				url: window.location.href,
				url_title: document.title,
				url_referrer: document.referrer,
				host: window.location.hostname,
				user_agent: navigator.userAgent,
				browser_language: navigator.language || navigator.userLanguage,
				viewport: `${window.innerWidth}x${window.innerHeight}`,
				Dextra: {
					eventName: event,
					eventProps: { ...eventProps }
				}
			};

			log('Send Metrics', data);

			const { promise } = request(`https://${this.metricsDomain}/datapoints`, {
				method: 'post',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(data)
			});

			promise.then(r => log(r)).catch(e => log(e));
		}, 300);
	};

	//return custom tracker for GA if set (legacy) otherwise use regular tracker
	getGaTracker = (event = '') => {
		return this.customGaTracker ? `${this.customGaTracker}.${event}` : event;
	};

	//GA integration
	initGa = () => {
		if (!ga4IsActive() && isGa4Id(this.gaId)) {
			loadGa4(this.gaId);
		} else if (ga4IsActive() && this.gaId !== getAnalyticsIdFromPage()) {
			handleGa4Post('config', this.gaId, gtagConfig);
		} else if (
			window.ga &&
			isAnalyticsId(this.gaId) &&
			this.gaId !== getAnalyticsIdFromPage()
		) {
			this.customGaTracker = legacyGaConfig.name;
			window.ga('create', this.gaId, legacyGaConfig);
			window.ga(this.getGaTracker('set'), 'allowAdFeatures', false);
		} else if (!window.ga && isAnalyticsId(this.gaId)) {
			this.customGaTracker = legacyGaConfig.name;
			(function (i, s, o, g, r, a, m) {
				i['GoogleAnalyticsObject'] = r;
				(i[r] =
					i[r] ||
					function () {
						(i[r].q = i[r].q || []).push(arguments);
					}),
					(i[r].l = 1 * new Date());
				(a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
				a.async = 1;
				a.src = g;
				m.parentNode.insertBefore(a, m);
			})(
				window,
				document,
				'script',
				'https://www.google-analytics.com/analytics.js',
				'ga'
			);
			window.ga('create', this.gaId, legacyGaConfig);
			window.ga(this.getGaTracker('set'), 'allowAdFeatures', false);
		}
	};

	isNonInteractionEvent = (action, eventProps = {}) => {
		const { trigger_method } = eventProps;

		const nonInteractionActions = ['form shown'];
		const nonInteractionTriggerMethods = ['proactive'];

		if (
			nonInteractionActions.indexOf(action.toLowerCase()) > -1 &&
			nonInteractionTriggerMethods.indexOf(trigger_method) > -1
		) {
			return true;
		}

		return false;
	};

	sendGa = (
		{ action, label, categoryExtra, feedbackQuestion, feedbackValue },
		eventProps = {}
	) => {
		const formName = `Mopinion Form - ${this.surveyName}`;

		log('Send Analytics', action);

		if (ga4IsActive() || isGa4Id(this.gaId)) {
			sendGa4WhenReady(() => {
				handleGa4Post('event', action, {
					mop_form_name: formName,
					send_to: this.gaId,
					...(feedbackQuestion &&
						feedbackValue && {
							mop_feedback_question: feedbackQuestion,
							mop_feedback_value: feedbackValue
						}),
					...(this.isNonInteractionEvent(action, eventProps) && {
						non_interaction: true
					})
				});
			});
		} else {
			const category = `Mopinion Form - ${this.surveyName}${
				categoryExtra ? categoryExtra : ''
			}`;
			sendLegacyGaWhenReady(() => {
				window.ga(this.getGaTracker('send'), {
					hitType: 'event',
					eventCategory: category,
					eventAction: action,
					...(label && {
						eventLabel: label
					}),
					...(this.isNonInteractionEvent(action, eventProps) && {
						nonInteraction: true
					})
				});
			});
		}
	};

	sendFeedbackGa = (feedback = []) => {
		feedback.forEach((feedbackItem, i) => {
			if (feedbackItem.title) {
				if (ga4IsActive() || isGa4Id(this.gaId)) {
					this.sendGa({
						action: 'Feedback data',
						feedbackQuestion: `Q${i + 1} - ${feedbackItem.title}`,
						feedbackValue: feedbackItem.value
					});
				} else {
					this.sendGa({
						action: `Q${i + 1} - ${feedbackItem.title}`,
						label: feedbackItem.value,
						categoryExtra: ' - Feedback'
					});
				}
			}
		});
	};
}
